import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const SummaryInput = z.object({
  query: z.string().trim().min(2).max(120),
  products: z
    .array(
      z.object({
        id: z.string().max(64),
        name: z.string().max(240),
        price: z.number().nullable(),
        brand: z.string().max(120).nullable(),
        category: z.string().max(160).nullable(),
      }),
    )
    .min(1)
    .max(20),
});

export type AiSearchSummary = {
  summary: string;
  picks: { id: string; reason: string }[];
  followUp: string;
};

const summaryCache = new Map<string, AiSearchSummary>();

async function rateLimited(endpoint: string, max: number) {
  const { getRequest } = await import("@tanstack/react-start/server");
  const { getClientIp, checkRateLimit } = await import("./abuse-guard.server");
  const ip = getClientIp(getRequest());
  return !(await checkRateLimit(endpoint, ip, { max, windowMinutes: 10 })).allowed;
}

/** Δύο γραμμές σύνοψη + 3 επιλογές, μόνο από τα προϊόντα που βρήκε η αναζήτηση. */
export const aiSearchSummary = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => SummaryInput.parse(d))
  .handler(async ({ data }): Promise<{ result: AiSearchSummary | null; error?: string }> => {
    const key = data.query.toLowerCase() + "|" + data.products.map((p) => p.id).join(",");
    const hit = summaryCache.get(key);
    if (hit) return { result: hit };
    if (await rateLimited("ai-search-summary", 60)) return { result: null, error: "rate" };
    const { aiJson, AiSearchBlocked } = await import("./ai-search.server");
    const list = data.products
      .map((p) => `${p.id} | ${p.name} | ${p.price != null ? p.price.toFixed(2) + "€" : "-"} | ${p.brand ?? ""} | ${p.category ?? ""}`)
      .join("\n");
    try {
      const r = await aiJson<AiSearchSummary>(
        "Είσαι ο βοηθός αγορών του ελληνικού eshop MrPanda. Γράφεις στα ελληνικά, φιλικά και σύντομα. " +
          "Με βάση ΜΟΝΟ τη λίστα προϊόντων που σου δίνεται: (1) summary: 1-2 προτάσεις (έως 220 χαρακτήρες) για το τι διαθέτουμε για την αναζήτηση, χωρίς να επινοείς χαρακτηριστικά, τιμές ή προσφορές. " +
          "(2) picks: ακριβώς 3 (ή λιγότερα αν υπάρχουν λιγότερα) id από τη λίστα που ταιριάζουν καλύτερα, με reason έως 70 χαρακτήρες βασισμένο μόνο στο όνομα/τιμή/μάρκα. " +
          "(3) followUp: μία σύντομη ερώτηση που βοηθά τον πελάτη να διαλέξει.",
        `Αναζήτηση πελάτη: «${data.query}»\nΠροϊόντα (id | όνομα | τιμή | μάρκα | κατηγορία):\n${list}`,
        {
          type: "object",
          additionalProperties: false,
          required: ["summary", "picks", "followUp"],
          properties: {
            summary: { type: "string" },
            followUp: { type: "string" },
            picks: {
              type: "array",
              items: {
                type: "object",
                additionalProperties: false,
                required: ["id", "reason"],
                properties: { id: { type: "string" }, reason: { type: "string" } },
              },
            },
          },
        },
      );
      if (!r) return { result: null, error: "unavailable" };
      const ids = new Set(data.products.map((p) => p.id));
      const clean: AiSearchSummary = {
        summary: r.summary.slice(0, 300),
        followUp: r.followUp.slice(0, 160),
        picks: r.picks.filter((p) => ids.has(p.id)).slice(0, 3).map((p) => ({ id: p.id, reason: p.reason.slice(0, 100) })),
      };
      if (summaryCache.size > 500) summaryCache.clear();
      summaryCache.set(key, clean);
      return { result: clean };
    } catch (e) {
      if (e instanceof AiSearchBlocked) return { result: null, error: "blocked" };
      console.error("aiSearchSummary", e);
      return { result: null, error: "unavailable" };
    }
  });

const ImageInput = z.object({
  dataUrl: z
    .string()
    .max(3_000_000)
    .regex(/^data:image\/(jpeg|png|webp);base64,/),
});

/** Αναγνωρίζει το προϊόν σε μια φωτογραφία και επιστρέφει λέξεις αναζήτησης. */
export const imageToSearchQuery = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => ImageInput.parse(d))
  .handler(async ({ data }): Promise<{ query: string | null; error?: string }> => {
    if (await rateLimited("ai-search-image", 15)) return { query: null, error: "rate" };
    const { aiJson, AiSearchBlocked } = await import("./ai-search.server");
    try {
      const r = await aiJson<{ query: string }>(
        "Αναγνώρισε το καταναλωτικό προϊόν της φωτογραφίας για αναζήτηση σε ελληνικό eshop (τρόφιμα, ποτά, καλλυντικά, είδη σπιτιού κ.ά.). " +
          "Επέστρεψε query: 2-5 λέξεις αναζήτησης — μάρκα αν διαβάζεται και τύπος προϊόντος στα ελληνικά (π.χ. «Barilla μακαρόνια», «αποσμητικό spray»). " +
          "Αν δεν φαίνεται προϊόν, επέστρεψε κενό string.",
        [
          { type: "input_text", text: "Τι προϊόν είναι αυτό;" },
          { type: "input_image", image_url: data.dataUrl },
        ],
        { type: "object", additionalProperties: false, required: ["query"], properties: { query: { type: "string" } } },
      );
      const q = r?.query?.trim().slice(0, 80) ?? "";
      return { query: q || null, error: q ? undefined : "notfound" };
    } catch (e) {
      if (e instanceof AiSearchBlocked) return { query: null, error: "blocked" };
      console.error("imageToSearchQuery", e);
      return { query: null, error: "unavailable" };
    }
  });

const LogInput = z.object({
  query: z.string().trim().min(2).max(120),
  results: z.number().int().min(0).max(100000),
  source: z.enum(["text", "image"]).default("text"),
});

/** Καταγράφει αναζητήσεις (ιδίως όσες δεν βρήκαν τίποτα) για τη λίστα «χωρίς αποτέλεσμα». */
export const logSearch = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => LogInput.parse(d))
  .handler(async ({ data }) => {
    try {
      if (await rateLimited("search-log", 120)) return { ok: false };
      const { gameCouponDb } = await import("./game-coupons.server");
      const norm = data.query.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim();
      const { error } = await gameCouponDb()
        .from("search_log" as never)
        .insert({ query: data.query, query_norm: norm, results: data.results, source: data.source } as never);
      if (error) console.error("logSearch", error.message);
      return { ok: !error };
    } catch (e) {
      console.error("logSearch", e);
      return { ok: false };
    }
  });
